import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { RoomEnvironment } from "three/addons/environments/RoomEnvironment.js";
import { terrainFallStep, terrainFlightAcceptsKeys, terrainLookDelta, terrainLookGesture, terrainWheelGesture, terrainZoomFov, withPreservedCameraTransform } from "./terrainNavigation.js";
import { buildRadialStructurePreviewModel, buildRadialStructureSourceSegmentPreviewModel } from "./radialStructurePreview.js";
import { buildStackedMegastructurePreview } from "./stackedMegastructurePreview.js";

/**
 * The 3D stage. Render modes match what asset-gen tools (Meshy, Sketchfab,
 * the glTF sample viewer) offer: full PBR, wireframe (solid + overlay),
 * normals, clay, UV checker, and per-channel texture isolation.
 */

export const MODES = [
  ["shaded", "Shaded"],
  ["wireover", "Wire+Shaded"],
  ["wireframe", "Wireframe"],
  ["clay", "Clay"],
  ["normals", "Normals"],
  ["uv", "UV Checker"],
  ["basecolor", "Base Color"],
  ["normalmap", "Normal Map"],
  ["roughness", "Roughness"],
  ["metallic", "Metallic"],
  ["ao", "AO"],
  ["emissive", "Emissive"],
];

const VIEWPORT_NEAR_CLIP_M = 0.1;

/**
 * Keep the studio usable on machines where the browser disables WebGL (remote
 * desktops, hardened CI browsers, and some virtual GPUs). Terrain Lab gets a
 * useful top-down live preview; the rest of the studio gets a clear diagnostic
 * instead of aborting application startup.
 */
class CanvasFallbackRenderer {
  constructor() {
    this.domElement = document.createElement("canvas");
    this.domElement.className = "viewer-canvas-fallback";
    this.domElement.setAttribute("aria-label", "2D terrain preview — WebGL is unavailable");
    this.context = this.domElement.getContext("2d");
    this.isFallbackRenderer = true;
    this.toneMappingExposure = 1;
    this.pixelRatio = 1;
    this.terrain = null;
    this.state = null;
    this.animationFrame = null;
  }

  setPixelRatio(value) {
    this.pixelRatio = Math.max(1, Number(value) || 1);
  }

  setSize(width, height) {
    const w = Math.max(1, Math.round(width));
    const h = Math.max(1, Math.round(height));
    this.domElement.width = Math.round(w * this.pixelRatio);
    this.domElement.height = Math.round(h * this.pixelRatio);
    this.domElement.style.width = `${w}px`;
    this.domElement.style.height = `${h}px`;
    this.draw();
  }

  setAnimationLoop(callback) {
    cancelAnimationFrame(this.animationFrame);
    if (!callback) return;
    const frame = (now) => {
      callback(now);
      this.animationFrame = requestAnimationFrame(frame);
    };
    this.animationFrame = requestAnimationFrame(frame);
  }

  render() {
    // The fallback is data-driven, so redraw only when its terrain changes or
    // the canvas is resized instead of repainting every animation frame.
  }

  setTerrain(terrain, state) {
    this.terrain = terrain;
    this.state = state;
    this.draw();
  }

  clearTerrain() {
    this.terrain = null;
    this.state = null;
    this.draw();
  }

  draw() {
    const ctx = this.context;
    const width = this.domElement.width;
    const height = this.domElement.height;
    if (!ctx || !width || !height) return;
    ctx.fillStyle = "#101014";
    ctx.fillRect(0, 0, width, height);
    if (!this.terrain || !this.state) {
      ctx.fillStyle = "#8f8c96";
      ctx.font = `${Math.max(13, Math.round(14 * this.pixelRatio))}px ui-sans-serif, sans-serif`;
      ctx.textAlign = "center";
      ctx.fillText("3D preview unavailable — WebGL is disabled", width / 2, height / 2);
      return;
    }

    const { terrain, state } = this;
    const resolution = terrain.resolution;
    const image = ctx.createImageData(resolution, resolution);
    const range = Math.max(terrain.max - terrain.min, 1);
    const walkable = state.traversal?.walkableMaxSlopeDeg ?? 32;
    const climbable = Math.max(state.traversal?.climbableMaxSlopeDeg ?? 52, walkable);
    const step = Math.max(terrain.cellSize, Number.EPSILON);
    for (let row = 0; row < resolution; row++) {
      for (let column = 0; column < resolution; column++) {
        const source = row * resolution + column;
        const target = ((resolution - 1 - row) * resolution + column) * 4;
        if (!terrain.inside[source]) {
          image.data[target + 3] = 0;
          continue;
        }
        const left = terrain.heights[row * resolution + Math.max(0, column - 1)];
        const right = terrain.heights[row * resolution + Math.min(resolution - 1, column + 1)];
        const down = terrain.heights[Math.max(0, row - 1) * resolution + column];
        const up = terrain.heights[Math.min(resolution - 1, row + 1) * resolution + column];
        const slope = Math.atan(Math.hypot(right - left, up - down) / (2 * step)) * (180 / Math.PI);
        const normalized = (terrain.heights[source] - terrain.min) / range;
        let color;
        if (terrain.heights[source] <= state.waterLevel) color = [39, 111, 145];
        else if (slope <= walkable) color = [52 + normalized * 64, 118 + normalized * 63, 76 + normalized * 45];
        else if (slope <= climbable) color = [184 + normalized * 43, 117 + normalized * 44, 48];
        else color = [116 + normalized * 58, 74 + normalized * 45, 65 + normalized * 42];
        image.data[target] = color[0];
        image.data[target + 1] = color[1];
        image.data[target + 2] = color[2];
        image.data[target + 3] = 255;
      }
    }

    const side = Math.min(width, height) * 0.86;
    const x = (width - side) / 2;
    const y = (height - side) / 2;
    const buffer = document.createElement("canvas");
    buffer.width = resolution;
    buffer.height = resolution;
    buffer.getContext("2d").putImageData(image, 0, 0);
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(buffer, x, y, side, side);

    const project = (point) => ({
      x: x + ((point.x / state.size) + 0.5) * side,
      y: y + (0.5 - point.z / state.size) * side,
    });
    const drawRing = (ring, stroke) => {
      if (!ring?.length) return;
      ctx.beginPath();
      ring.forEach((point, index) => {
        const p = project(point);
        if (index) ctx.lineTo(p.x, p.y); else ctx.moveTo(p.x, p.y);
      });
      ctx.closePath();
      ctx.strokeStyle = stroke;
      ctx.lineWidth = Math.max(1.5, 2 * this.pixelRatio);
      ctx.stroke();
    };
    drawRing(state.boundary?.outer, "#f2a44f");
    for (const hole of state.boundary?.holes ?? []) drawRing(hole, "#73c1cb");
    for (const poi of state.pois ?? []) {
      const p = project(poi);
      ctx.beginPath();
      ctx.arc(p.x, p.y, Math.max(3, 4.5 * this.pixelRatio), 0, Math.PI * 2);
      ctx.fillStyle = poi.manual ? "#f2a44f" : "#f4e5b8";
      ctx.fill();
      ctx.strokeStyle = "#18171b";
      ctx.lineWidth = Math.max(1, this.pixelRatio);
      ctx.stroke();
    }
    ctx.fillStyle = "rgba(16, 16, 20, 0.82)";
    ctx.fillRect(x + 10 * this.pixelRatio, y + 10 * this.pixelRatio, 196 * this.pixelRatio, 27 * this.pixelRatio);
    ctx.fillStyle = "#f4e5b8";
    ctx.textAlign = "left";
    ctx.font = `${Math.max(11, 11 * this.pixelRatio)}px ui-sans-serif, sans-serif`;
    ctx.fillText("2D LIVE PREVIEW · WEBGL OFF", x + 19 * this.pixelRatio, y + 28 * this.pixelRatio);
  }
}

/** Isolate one channel of a (possibly packed) texture as grayscale.
 * glTF packs roughness in G and metallic in B of the same map. */
function channelMaterial(texture, channel, fallback) {
  return new THREE.ShaderMaterial({
    uniforms: {
      map: { value: texture ?? null },
      channel: { value: channel },
      hasMap: { value: texture ? 1 : 0 },
      fallback: { value: fallback },
    },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      uniform sampler2D map;
      uniform int channel;
      uniform int hasMap;
      uniform float fallback;
      varying vec2 vUv;
      void main() {
        float v = fallback;
        if (hasMap == 1) {
          vec4 t = texture2D(map, vUv);
          v = channel == 0 ? t.r : channel == 1 ? t.g : t.b;
        }
        gl_FragColor = vec4(vec3(v), 1.0);
      }`,
  });
}

function makeLabel(text, x, z, color) {
  const cv = document.createElement("canvas");
  cv.width = 512; cv.height = 128;
  const ctx = cv.getContext("2d");
  ctx.font = "600 56px ui-sans-serif, sans-serif";
  ctx.textAlign = "center";
  ctx.fillStyle = `#${color.toString(16).padStart(6, "0")}`;
  ctx.fillText(text.slice(0, 24), 256, 80);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false }));
  sprite.scale.set(4, 1, 1);
  sprite.position.set(x, 2.2, z);
  return sprite;
}

/** Wedge for a blockout ramp: footprint w(x)×d(z), rising to height h
 * toward `dir` (world axes: +x east, -x west, +y → three +z, -y → three -z).
 * Min corner at local origin, matching how solids are positioned. */
function makeRampGeometry(w, h, d, dir) {
  const ht = (x, z) => {
    const t = dir === "+x" ? x / w : dir === "-x" ? 1 - x / w : dir === "+y" ? z / d : 1 - z / d;
    return Math.max(0.002, t * h); // tiny lip at the low edge avoids degenerate faces
  };
  const a = [0, 0, 0], b = [w, 0, 0], c = [w, 0, d], e = [0, 0, d];
  const A = [0, ht(0, 0), 0], B = [w, ht(w, 0), 0], C = [w, ht(w, d), d], E = [0, ht(0, d), d];
  const tris = [
    [a, c, b], [a, e, c], // bottom
    [A, B, C], [A, C, E], // sloped top
    [a, b, B], [a, B, A], // side z=0
    [e, C, c], [e, E, C], // side z=d
    [a, A, E], [a, E, e], // side x=0
    [b, c, C], [b, C, B], // side x=w
  ];
  const pos = new Float32Array(tris.flat(2));
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  geo.computeVertexNormals();
  return geo;
}

/** Numbered feedback pin: a circle sprite + a thin stalk down to the floor. */
function makePin(number, colorHex) {
  const cv = document.createElement("canvas");
  cv.width = cv.height = 128;
  const ctx = cv.getContext("2d");
  ctx.beginPath(); ctx.arc(64, 64, 52, 0, Math.PI * 2);
  ctx.fillStyle = `#${colorHex.toString(16).padStart(6, "0")}`;
  ctx.fill();
  ctx.lineWidth = 8; ctx.strokeStyle = "#101014"; ctx.stroke();
  ctx.fillStyle = "#101014";
  ctx.font = "700 60px ui-sans-serif, sans-serif";
  ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.fillText(String(number), 64, 68);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false }));
  sprite.scale.setScalar(1.8);
  return sprite;
}

function makeCheckerTexture() {
  const size = 512, cells = 8;
  const cv = document.createElement("canvas");
  cv.width = cv.height = size;
  const ctx = cv.getContext("2d");
  const cell = size / cells;
  const colors = ["#8a8a96", "#3c3c46"];
  for (let y = 0; y < cells; y++)
    for (let x = 0; x < cells; x++) {
      ctx.fillStyle = colors[(x + y) % 2];
      ctx.fillRect(x * cell, y * cell, cell, cell);
    }
  ctx.fillStyle = "#e8b34b";
  ctx.font = `${cell * 0.5}px monospace`;
  ctx.textBaseline = "top";
  for (let y = 0; y < cells; y++)
    for (let x = 0; x < cells; x++)
      ctx.fillText(`${x}${y}`, x * cell + 4, y * cell + 4);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  return tex;
}

function disposeMaterial(material) {
  if (!material) return;
  for (const value of Object.values(material)) if (value?.isTexture) value.dispose?.();
  material.dispose?.();
}

function disposeObject3D(object) {
  if (!object) return;
  const geometries = new Set();
  const materials = new Set();
  object.traverse?.((node) => {
    if (node.geometry) geometries.add(node.geometry);
    const nodeMaterials = Array.isArray(node.material) ? node.material : [node.material];
    for (const material of nodeMaterials) if (material) materials.add(material);
  });
  for (const geometry of geometries) geometry.dispose?.();
  for (const material of materials) disposeMaterial(material);
}

function cancelledLoadError() {
  return new DOMException("Viewer load superseded by a newer route", "AbortError");
}

export class Viewer {
  constructor(container, { fileUrl = (key) => `/files/${key}` } = {}) {
    this.container = container;
    this.fileUrl = fileUrl;
    this.loadGeneration = 0;
    const probe = document.createElement("canvas");
    const supportsWebGL = Boolean(probe.getContext("webgl2") || probe.getContext("webgl"));
    if (supportsWebGL) {
      try {
        this.renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
      } catch {
        this.renderer = new CanvasFallbackRenderer();
      }
    } else {
      this.renderer = new CanvasFallbackRenderer();
    }
    this.webglAvailable = !this.renderer.isFallbackRenderer;
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    container.appendChild(this.renderer.domElement);

    this.scene = new THREE.Scene();
    this.bgColor = new THREE.Color(0x101014);
    this.scene.background = this.bgColor;

    this.camera = new THREE.PerspectiveCamera(45, 1, VIEWPORT_NEAR_CLIP_M, 500);
    this.camera.position.set(0.6, 0.4, 0.9);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.autoRotateSpeed = 2.5;
    this.controls.zoomSpeed = 2;
    this.controls.zoomToCursor = true;
    this._orbitGestureScale = null;

    // Free-flight navigation. Orbit remains the default for asset inspection;
    // fly mode uses pointer lock so mouse movement can look continuously.
    this.fly = {
      enabled: false,
      active: false,
      keys: new Set(),
      speed: 4,
      travelMultiplier: 10,
      precisionMultiplier: 1,
      orbitDistance: null,
      sensitivity: 0.002,
    };
    // Terrain authoring navigation deliberately does not use pointer lock:
    // left-click belongs to terrain anchors and draggable POIs, while holding
    // the right mouse button owns camera look. WASD remains available at all
    // times unless the user is typing in a field.
    this.walk = {
      enabled: false,
      lookActive: false,
      lookPointerId: null,
      lookClientPoint: null,
      keys: new Set(),
      speed: 24,
      boost: 3.5,
      sensitivity: 0.003,
      eyeHeight: 5,
      heightAt: null,
      halfSize: 500,
      falling: false,
      verticalVelocity: 0,
      gravity: 32,
      terminalVelocity: 90,
    };
    this.onFlyChange = null;
    this.onWalkChange = null;
    this._flyEuler = new THREE.Euler(0, 0, 0, "YXZ");
    this._flyForward = new THREE.Vector3();
    this._flyRight = new THREE.Vector3();
    this._flyMove = new THREE.Vector3();
    const flyCodes = new Set([
      "KeyW", "KeyA", "KeyS", "KeyD",
      "ShiftLeft", "ShiftRight",
      "ControlLeft", "ControlRight",
      "Space",
    ]);
    document.addEventListener("keydown", (e) => {
      if (!flyCodes.has(e.code)) return;
      const typing = e.target?.matches?.("input, textarea, select, [contenteditable='true']");
      if (this.walk.enabled && !typing) {
        e.preventDefault();
        this.walk.keys.add(e.code);
        return;
      }
      // Pointer lock owns mouse-look, not keyboard flight. Chrome can decline
      // pointer lock on macOS, but Space/Ctrl/WASD must still move the free
      // camera whenever the mode itself is enabled.
      if (!terrainFlightAcceptsKeys({ enabled: this.fly.enabled, typing })) return;
      e.preventDefault();
      this.fly.keys.add(e.code);
    });
    document.addEventListener("keyup", (e) => {
      if (!flyCodes.has(e.code)) return;
      this.fly.keys.delete(e.code);
      this.walk.keys.delete(e.code);
    });
    document.addEventListener("pointermove", (e) => {
      if (!this.walk.enabled || !this.walk.lookActive) return;
      const delta = terrainLookDelta(e, this.walk.lookPointerId, this.walk.lookClientPoint);
      if (!delta) return;
      if (delta.clientX !== null && delta.clientY !== null) this.walk.lookClientPoint = { x: delta.clientX, y: delta.clientY };
      this.applyWalkLook(delta.movementX, delta.movementY);
    });
    document.addEventListener("mousemove", (e) => {
      if (!this.fly.enabled || !this.fly.active) return;
      this._flyEuler.setFromQuaternion(this.camera.quaternion, "YXZ");
      this._flyEuler.y -= e.movementX * this.fly.sensitivity;
      this._flyEuler.x -= e.movementY * this.fly.sensitivity;
      this._flyEuler.x = THREE.MathUtils.clamp(this._flyEuler.x, -Math.PI / 2 + 0.01, Math.PI / 2 - 0.01);
      this._flyEuler.z = 0;
      this.camera.quaternion.setFromEuler(this._flyEuler);
    });
    document.addEventListener("pointerlockchange", () => {
      this.fly.active = document.pointerLockElement === this.renderer.domElement;
      if (!this.fly.active) this.fly.keys.clear();
      this.onFlyChange?.({ enabled: this.fly.enabled, active: this.fly.active, speed: this.fly.speed });
    });

    // feedback pins — click (not drag) raycasts against pin sprites
    this.pinGroup = new THREE.Group();
    this.scene.add(this.pinGroup);
    this.onPinClick = null;
    this._raycaster = new THREE.Raycaster();
    let downAt = null;
    this.renderer.domElement.addEventListener("pointerdown", (e) => {
      const lookGesture = terrainLookGesture(e);
      if (this.walk.enabled && lookGesture.active) {
        e.preventDefault();
        this.walk.lookActive = true;
        this.walk.lookPointerId = e.pointerId;
        this.walk.lookClientPoint = { x: e.clientX, y: e.clientY };
        this.renderer.domElement.setPointerCapture?.(e.pointerId);
        this.emitWalkChange({ enabled: true, looking: true, speed: this.walk.speed, input: lookGesture.input });
        downAt = null;
        return;
      }
      if (this.fly.enabled) {
        downAt = null;
        this.lockFlyPointer();
        return;
      }
      downAt = [e.clientX, e.clientY];
    });
    this.renderer.domElement.addEventListener("pointerup", (e) => {
      if (this.walk.enabled && e.pointerId === this.walk.lookPointerId) {
        this.walk.lookActive = false;
        this.walk.lookPointerId = null;
        this.walk.lookClientPoint = null;
        this.renderer.domElement.releasePointerCapture?.(e.pointerId);
        this.emitWalkChange({ enabled: true, looking: false, speed: this.walk.speed });
        return;
      }
      if (this.fly.enabled) return;
      if (!downAt || Math.hypot(e.clientX - downAt[0], e.clientY - downAt[1]) > 5) return;
      const r = this.renderer.domElement.getBoundingClientRect();
      const ndc = new THREE.Vector2(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
      this._raycaster.setFromCamera(ndc, this.camera);
      const hit = this._raycaster.intersectObjects(this.pinGroup.children, true)[0];
      if (hit && this.onPinClick) {
        let o = hit.object;
        while (o && !o.userData?.pinId) o = o.parent;
        if (o?.userData?.pinId) this.onPinClick(o.userData.pinId);
      }
    });
    this.renderer.domElement.addEventListener("pointercancel", () => {
      this.cancelWalkInput();
    });
    this.renderer.domElement.addEventListener("lostpointercapture", () => {
      if (this.walk.lookActive) this.cancelWalkInput();
    });
    this.renderer.domElement.addEventListener("wheel", (e) => {
      this.handleNavigationWheel(e);
    }, { passive: false });
    this.renderer.domElement.addEventListener("gesturestart", (e) => this.handleOrbitGestureStart(e), { passive: false });
    this.renderer.domElement.addEventListener("gesturechange", (e) => this.handleOrbitGestureChange(e), { passive: false });
    this.renderer.domElement.addEventListener("gestureend", () => { this._orbitGestureScale = null; });
    this.renderer.domElement.addEventListener("contextmenu", (e) => {
      if (this.walk.enabled) e.preventDefault();
    });
    window.addEventListener("blur", () => this.cancelWalkInput());
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) this.cancelWalkInput();
    });

    if (this.webglAvailable) {
      const pmrem = new THREE.PMREMGenerator(this.renderer);
      this.envTexture = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
      this.scene.environment = this.envTexture;
    } else {
      this.envTexture = null;
    }

    this.grid = new THREE.GridHelper(2, 20, 0x444455, 0x26262f);
    this.scene.add(this.grid);
    this.axes = new THREE.AxesHelper(0.5);
    this.axes.visible = false;
    this.scene.add(this.axes);

    this.checker = makeCheckerTexture();
    this.model = null;
    this.bboxHelper = null;
    this.wireOverlays = [];
    this.originals = new Map(); // mesh -> original material
    this.transientModeMaterials = new Set();
    this.mode = "shaded";
    this.toggles = { grid: true, axes: false, bbox: false, turntable: false, envBackground: false };
    this._terrainViewportActive = false;
    this._terrainInputHandlers = null;
    this._terrainGridWasVisible = null;
    this._terrainCameraMode = "orbit";
    this._terrainCharacterPose = null;
    this.onTerrainNavigationChange = null;

    this.loader = new GLTFLoader();

    const resize = () => {
      const { clientWidth: w, clientHeight: h } = container;
      if (!w || !h) return;
      this.renderer.setSize(w, h);
      this.camera.aspect = w / h;
      this.camera.updateProjectionMatrix();
    };
    new ResizeObserver(resize).observe(container);
    resize();

    let lastFrameAt = performance.now();
    this.renderer.setAnimationLoop((now) => {
      const deltaSeconds = Math.min(Math.max((now - lastFrameAt) / 1000, 0), 0.1);
      lastFrameAt = now;
      if (this.walk.enabled) {
        this.updateWalk(deltaSeconds);
      } else if (this.fly.enabled) {
        this.updateFly(deltaSeconds);
      } else {
        this.controls.autoRotate = this.toggles.turntable;
        this.controls.update();
      }
      this.renderer.render(this.scene, this.camera);
    });
  }

  lockFlyPointer() {
    if (!this.fly.enabled || document.pointerLockElement === this.renderer.domElement) return;
    try {
      const request = this.renderer.domElement.requestPointerLock();
      request?.catch?.(() => {});
    } catch {}
  }

  /**
   * Terrain Lab's viewport interface. It deliberately exposes terrain
   * behaviours rather than Three.js objects, so callers cannot own renderer
   * lifetime, scene replacement, or navigation input by accident.
   */
  activateTerrainViewport({ handlers = {}, onNavigationChange } = {}) {
    if (this._terrainViewportActive) this.deactivateTerrainViewport();
    this._terrainViewportActive = true;
    this._terrainGridWasVisible = this.grid.visible;
    this.grid.visible = false;
    this.onTerrainNavigationChange = onNavigationChange ?? null;
    this.setFlyMode(false);
    this.setWalkMode(false);
    this._terrainInputHandlers = { ...handlers };
    for (const [eventName, handler] of Object.entries(this._terrainInputHandlers)) {
      if (typeof handler !== "function") continue;
      const target = eventName === "keydown" ? document : this.renderer.domElement;
      target.addEventListener(eventName, handler);
    }
  }

  deactivateTerrainViewport() {
    const handlers = this._terrainInputHandlers ?? {};
    for (const [eventName, handler] of Object.entries(handlers)) {
      if (typeof handler !== "function") continue;
      const target = eventName === "keydown" ? document : this.renderer.domElement;
      target.removeEventListener(eventName, handler);
    }
    this._terrainInputHandlers = null;
    this.cancelWalkInput();
    this.setFlyMode(false);
    this.setWalkMode(false);
    if (this._terrainGridWasVisible !== null && this._terrainGridWasVisible !== undefined) {
      this.grid.visible = this._terrainGridWasVisible;
    }
    this._terrainGridWasVisible = null;
    this._terrainViewportActive = false;
    this._terrainCameraMode = "orbit";
    this._terrainCharacterPose = null;
    this.onTerrainNavigationChange = null;
  }

  isTerrainViewportActive() {
    return Boolean(this._terrainViewportActive);
  }

  emitWalkChange(change) {
    this.onWalkChange?.(change);
    this.onTerrainNavigationChange?.(change);
  }

  captureTerrainCameraPose() {
    return {
      position: this.camera.position.toArray(),
      quaternion: this.camera.quaternion.toArray(),
    };
  }

  setTerrainCameraPose(pose) {
    if (!pose?.position || !pose?.quaternion) return;
    this.camera.position.fromArray(pose.position);
    this.camera.quaternion.fromArray(pose.quaternion);
  }

  getTerrainCameraMode() {
    return this._terrainCameraMode ?? "orbit";
  }

  setTerrainCameraMode(mode, options = {}) {
    if (mode === "free") {
      if (this.getTerrainCameraMode() !== "free") this._terrainCharacterPose = this.captureTerrainCameraPose();
      this.setWalkMode(false);
      this.setFlySpeed(options.speed);
      this.setFlyMode(true);
      if (this._terrainCharacterPose) this.setTerrainCameraPose(this._terrainCharacterPose);
      this._terrainCameraMode = "free";
      return;
    }
    if (mode === "walk") {
      this.setFlyMode(false);
      const characterPose = this._terrainCharacterPose;
      if (characterPose) this.setTerrainCameraPose(characterPose);
      this.setWalkMode(true, options);
      if (characterPose) this.setTerrainCameraPose(characterPose);
      this._terrainCharacterPose = null;
      this._terrainCameraMode = "walk";
      return;
    }
    this.setFlyMode(false);
    this.setWalkMode(false);
    this._terrainCharacterPose = null;
    this._terrainCameraMode = "orbit";
  }

  setCameraClipping({ far } = {}) {
    const requestedFar = Number(far);
    if (Number.isFinite(requestedFar) && requestedFar > 0) this.camera.far = requestedFar;
    this.camera.near = Math.min(VIEWPORT_NEAR_CLIP_M, this.camera.far * 0.5);
    this.camera.updateProjectionMatrix();
  }

  setTerrainCameraClipping(clipping) {
    this.setCameraClipping(clipping);
  }

  setTerrainCameraLookAt(position, target) {
    this.camera.position.fromArray(position);
    this.camera.lookAt(...target);
  }

  getTerrainCameraHeading() {
    const direction = this.camera.getWorldDirection(new THREE.Vector3());
    return Math.atan2(direction.z, direction.x);
  }

  getTerrainCameraFov() {
    return this.camera.fov;
  }

  setTerrainRayFromPointer(event, raycaster) {
    const rect = this.renderer.domElement.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    );
    raycaster.setFromCamera(ndc, this.camera);
    return raycaster;
  }

  isTerrainLooking() {
    return Boolean(this.walk.lookActive);
  }

  captureTerrainPointer(pointerId) {
    this.renderer.domElement.setPointerCapture?.(pointerId);
  }

  releaseTerrainPointer(pointerId) {
    if (this.renderer.domElement.hasPointerCapture?.(pointerId)) this.renderer.domElement.releasePointerCapture?.(pointerId);
  }

  replaceTerrainScene({ group, terrainMesh, terrain, state, clipping } = {}) {
    this.clear();
    if (!group) return { replaced: false };
    this.model = group;
    this.scene.add(group);
    if (terrainMesh?.material) this.originals.set(terrainMesh, terrainMesh.material);
    this.wireOverlays = [];
    this.grid.visible = false;
    this.setTerrainCameraClipping(clipping ?? {});
    this.setFallbackTerrain(terrain, state);
    return { replaced: true };
  }

  handleNavigationWheel(event) {
    // Fly owns only mouse-look and movement. Trackpad/wheel input must not
    // alter the flight camera; normal OrbitControls owns zoom when Fly is off.
    if (this.fly.enabled) return false;
    if (!this.walk.enabled) return false;
    const gesture = terrainWheelGesture(event, this.renderer.domElement.clientHeight);
    event.preventDefault();
    if (gesture.type === "zoom") {
      this.camera.fov = terrainZoomFov(this.camera.fov, gesture.amount);
      this.camera.updateProjectionMatrix();
      this.emitWalkChange({ enabled: true, looking: false, speed: this.walk.speed, input: "zoom" });
      return true;
    }
    const boost = gesture.boosted ? this.walk.boost : 1;
    const distancePerPixel = (this.walk.speed * boost) / 240;
    this.moveWalk(gesture.right * distancePerPixel, gesture.forward * distancePerPixel);
    this.emitWalkChange({ enabled: true, looking: false, speed: this.walk.speed, input: "glide" });
    return true;
  }

  handleOrbitGestureStart(event) {
    if (this.fly.enabled || this.walk.enabled || !this.controls.enabled) return false;
    event.preventDefault();
    this._orbitGestureScale = Math.max(Number(event.scale) || 1, 0.01);
    return true;
  }

  handleOrbitGestureChange(event) {
    if (this.fly.enabled || this.walk.enabled || !this.controls.enabled || this._orbitGestureScale === null) return false;
    event.preventDefault();
    const nextScale = Math.max(Number(event.scale) || this._orbitGestureScale, 0.01);
    const offset = this.camera.position.clone().sub(this.controls.target);
    const nextDistance = THREE.MathUtils.clamp(
      offset.length() * (this._orbitGestureScale / nextScale),
      this.controls.minDistance,
      this.controls.maxDistance,
    );
    if (offset.lengthSq() > 0.000001) {
      offset.setLength(nextDistance);
      this.camera.position.copy(this.controls.target).add(offset);
      this.controls.update();
    }
    this._orbitGestureScale = nextScale;
    return true;
  }

  setFlyMode(on) {
    withPreservedCameraTransform(this.camera, () => {
      const nextEnabled = Boolean(on);
      if (nextEnabled && !this.fly.enabled) {
        this.fly.orbitDistance = this.camera.position.distanceTo(this.controls.target);
      }
      if (on && this.walk.enabled) this.setWalkMode(false);
      this.fly.enabled = nextEnabled;
      this.fly.keys.clear();
      this._orbitGestureScale = null;
      this.controls.enabled = !this.fly.enabled;
      this.controls.autoRotate = false;
      if (this.fly.enabled) {
        this.lockFlyPointer();
      } else {
        if (document.pointerLockElement === this.renderer.domElement) document.exitPointerLock();
        const direction = this.camera.getWorldDirection(new THREE.Vector3());
        const fullTravelOrbitDistance = this.fly.speed * (this.fly.travelMultiplier ?? 1) * 2;
        const targetDistance = Math.max(this.fly.orbitDistance ?? 0, fullTravelOrbitDistance, 1);
        this.controls.target.copy(this.camera.position).addScaledVector(direction, targetDistance);
        this.controls.update();
      }
    });
    this.onFlyChange?.({ enabled: this.fly.enabled, active: this.fly.active, speed: this.fly.speed });
  }

  setFlyScale(maxDimensionM) {
    this.setFlySpeed(maxDimensionM * 0.08);
  }

  setFlySpeed(metersPerSecond) {
    this.fly.speed = THREE.MathUtils.clamp(Number(metersPerSecond) || 1, 1, 100);
    this.onFlyChange?.({ enabled: this.fly.enabled, active: this.fly.active, speed: this.fly.speed });
  }

  updateFly(deltaSeconds) {
    if (!this.fly.enabled || deltaSeconds <= 0) return;
    const keys = this.fly.keys;
    const forwardAmount = Number(keys.has("KeyW")) - Number(keys.has("KeyS"));
    const rightAmount = Number(keys.has("KeyD")) - Number(keys.has("KeyA"));
    const upAmount = Number(keys.has("Space")) - Number(keys.has("ControlLeft") || keys.has("ControlRight"));
    if (!forwardAmount && !rightAmount && !upAmount) return;

    // W/S follow the complete view vector, including the camera pitch. This
    // is free flight: looking up or down changes the path of forward motion.
    // A/D use the camera's local X axis, so they remain camera-relative
    // strafe controls rather than inheriting forward's vertical component.
    this.camera.getWorldDirection(this._flyForward);
    if (this._flyForward.lengthSq() < 0.000001) this._flyForward.set(0, 0, -1);
    this._flyForward.normalize();
    this._flyRight.set(1, 0, 0).applyQuaternion(this.camera.quaternion).normalize();
    this._flyMove
      .set(0, upAmount, 0)
      .addScaledVector(this._flyForward, forwardAmount)
      .addScaledVector(this._flyRight, rightAmount)
      .normalize();

    const precisionMode = keys.has("ShiftLeft") || keys.has("ShiftRight");
    const speed = this.fly.speed * (precisionMode ? this.fly.precisionMultiplier : this.fly.travelMultiplier);
    this.camera.position.addScaledVector(this._flyMove, speed * deltaSeconds);
  }

  setWalkMode(on, { heightAt, halfSize, eyeHeight = 5, speed, dropToGround = false } = {}) {
    this.walk.enabled = Boolean(on);
    this.walk.keys.clear();
    this.walk.lookActive = false;
    this.walk.lookPointerId = null;
    this.walk.lookClientPoint = null;
    if (heightAt) this.walk.heightAt = heightAt;
    if (Number.isFinite(halfSize)) this.walk.halfSize = halfSize;
    if (Number.isFinite(eyeHeight)) this.walk.eyeHeight = eyeHeight;
    if (Number.isFinite(speed)) this.walk.speed = speed;
    if (this.walk.enabled) {
      if (this.fly.enabled) this.setFlyMode(false);
      this.controls.enabled = false;
      const ground = this.walk.heightAt?.(this.camera.position.x, this.camera.position.z);
      if (Number.isFinite(ground)) {
        const targetY = ground + this.walk.eyeHeight;
        this.walk.falling = Boolean(dropToGround && this.camera.position.y > targetY);
        this.walk.verticalVelocity = 0;
        if (!this.walk.falling || this.camera.position.y < targetY) this.camera.position.y = targetY;
      }
    } else {
      this.walk.falling = false;
      this.walk.verticalVelocity = 0;
      withPreservedCameraTransform(this.camera, () => {
        this.controls.enabled = !this.fly.enabled;
        const direction = this.camera.getWorldDirection(new THREE.Vector3());
        this.controls.target.copy(this.camera.position).addScaledVector(direction, Math.max(this.walk.speed * 2, 1));
        this.controls.update();
      });
    }
    this.emitWalkChange({ enabled: this.walk.enabled, looking: false, falling: this.walk.falling, speed: this.walk.speed, input: this.walk.falling ? "fall" : undefined });
  }

  cancelWalkInput() {
    if (!this.walk.enabled) return;
    const changed = this.walk.lookActive || this.walk.keys.size > 0;
    this.walk.lookActive = false;
    this.walk.lookPointerId = null;
    this.walk.lookClientPoint = null;
    this.walk.keys.clear();
    if (changed) this.emitWalkChange({ enabled: true, looking: false, speed: this.walk.speed });
  }

  applyWalkLook(movementX, movementY) {
    this._flyEuler.setFromQuaternion(this.camera.quaternion, "YXZ");
    this._flyEuler.y -= movementX * this.walk.sensitivity;
    this._flyEuler.x -= movementY * this.walk.sensitivity;
    this._flyEuler.x = THREE.MathUtils.clamp(this._flyEuler.x, -Math.PI / 2 + 0.04, Math.PI / 2 - 0.04);
    this._flyEuler.z = 0;
    this.camera.quaternion.setFromEuler(this._flyEuler);
  }

  moveWalk(rightDistance, forwardDistance) {
    this.camera.getWorldDirection(this._flyForward);
    this._flyForward.y = 0;
    if (this._flyForward.lengthSq() < 0.000001) this._flyForward.set(0, 0, -1);
    this._flyForward.normalize();
    this._flyRight.crossVectors(this._flyForward, this.camera.up).normalize();
    this.camera.position
      .addScaledVector(this._flyRight, rightDistance)
      .addScaledVector(this._flyForward, forwardDistance);
    const limit = Math.max(this.walk.halfSize - 2, 1);
    this.camera.position.x = THREE.MathUtils.clamp(this.camera.position.x, -limit, limit);
    this.camera.position.z = THREE.MathUtils.clamp(this.camera.position.z, -limit, limit);
    const ground = this.walk.heightAt?.(this.camera.position.x, this.camera.position.z);
    if (Number.isFinite(ground)) {
      const targetY = ground + this.walk.eyeHeight;
      if (!this.walk.falling) this.camera.position.y = targetY;
      else if (this.camera.position.y <= targetY) {
        this.camera.position.y = targetY;
        this.walk.falling = false;
        this.walk.verticalVelocity = 0;
        this.emitWalkChange({ enabled: true, looking: false, falling: false, speed: this.walk.speed, input: "landed" });
      }
    }
  }

  updateWalk(deltaSeconds) {
    if (deltaSeconds <= 0) return;
    const keys = this.walk.keys;
    const forwardAmount = Number(keys.has("KeyW")) - Number(keys.has("KeyS"));
    const rightAmount = Number(keys.has("KeyD")) - Number(keys.has("KeyA"));
    if (forwardAmount || rightAmount) {
      this._flyMove.set(rightAmount, 0, forwardAmount).normalize();
      const boosted = keys.has("ShiftLeft") || keys.has("ShiftRight");
      const speed = this.walk.speed * (boosted ? this.walk.boost : 1);
      this.moveWalk(this._flyMove.x * speed * deltaSeconds, this._flyMove.z * speed * deltaSeconds);
    }
    if (!this.walk.falling) return;
    const ground = this.walk.heightAt?.(this.camera.position.x, this.camera.position.z);
    if (!Number.isFinite(ground)) return;
    const step = terrainFallStep({
      currentY: this.camera.position.y,
      targetY: ground + this.walk.eyeHeight,
      velocity: this.walk.verticalVelocity,
      deltaSeconds,
      gravity: this.walk.gravity,
      terminalVelocity: this.walk.terminalVelocity,
    });
    this.camera.position.y = step.y;
    this.walk.verticalVelocity = step.velocity;
    if (step.landed) {
      this.walk.falling = false;
      this.emitWalkChange({ enabled: true, looking: false, falling: false, speed: this.walk.speed, input: "landed" });
    }
  }

  clear() {
    this.loadGeneration += 1;
    this.renderer.clearTerrain?.();
    for (const child of [...this.pinGroup.children]) disposeObject3D(child);
    this.pinGroup.clear();
    const geometries = new Set();
    const materials = new Set();
    if (this.model) {
      this.scene.remove(this.model);
      this.model.traverse((o) => {
        if (o.geometry) geometries.add(o.geometry);
        const mats = Array.isArray(o.material) ? o.material : [o.material];
        for (const material of mats) if (material) materials.add(material);
      });
    }
    for (const original of this.originals.values()) {
      const mats = Array.isArray(original) ? original : [original];
      for (const material of mats) if (material) materials.add(material);
    }
    for (const material of this.transientModeMaterials) materials.add(material);
    for (const geometry of geometries) geometry.dispose?.();
    for (const material of materials) disposeMaterial(material);
    if (this.bboxHelper) {
      this.scene.remove(this.bboxHelper);
      this.bboxHelper.geometry?.dispose?.();
      disposeMaterial(this.bboxHelper.material);
    }
    this.model = null;
    this.bboxHelper = null;
    this.wireOverlays = [];
    this.originals.clear();
    this.transientModeMaterials.clear();
  }

  captureCameraPose() {
    return {
      position: this.camera.position.toArray(),
      quaternion: this.camera.quaternion.toArray(),
      target: this.controls.target.toArray(),
      fov: this.camera.fov,
      near: this.camera.near,
      far: this.camera.far,
    };
  }

  restoreCameraPose(pose) {
    if (!pose) return;
    this.camera.position.fromArray(pose.position);
    this.camera.quaternion.fromArray(pose.quaternion);
    this.controls.target.fromArray(pose.target);
    this.camera.fov = pose.fov;
    this.setCameraClipping({ far: pose.far });
    if (!this.fly.enabled) this.controls.update();
  }

  /** Live candidate visualization. This is intentionally independent from the Blender GLB. */
  loadStackedMegastructure(scene, { keepCamera = false } = {}) {
    const pose = keepCamera ? this.captureCameraPose() : null;
    this.clear();
    const preview = buildStackedMegastructurePreview(scene);
    const group = new THREE.Group();
    group.name = `${preview.state.label} · live Three.js candidate`;
    group.userData = { previewKind: "stacked_megastructure_visualization", preview };
    this.model = group;
    this.scene.add(group);

    const materials = {
      landscape: new THREE.MeshStandardMaterial({ color: preview.state.appearance.landscapeColor, roughness: 0.98, metalness: 0 }),
      disk: new THREE.MeshStandardMaterial({ color: preview.state.appearance.diskColor, roughness: 0.72, metalness: 0.18 }),
      wall: new THREE.MeshStandardMaterial({ color: preview.state.appearance.wallColor, roughness: 0.82, metalness: 0.06, side: THREE.DoubleSide }),
      pillar: new THREE.MeshStandardMaterial({ color: preview.state.appearance.pillarColor, roughness: 0.68, metalness: 0.12 }),
    };
    const ground = new THREE.Mesh(
      new THREE.BoxGeometry(preview.ground.widthM, preview.ground.thicknessM, preview.ground.depthM),
      materials.landscape,
    );
    ground.position.set(preview.ground.centreXM, preview.ground.centreYM, preview.ground.centreZM);
    ground.visible = preview.ground.visible;
    ground.name = "Landscape · live proxy";
    group.add(ground);

    const diskGeometry = new THREE.CylinderGeometry(preview.disk.radiusM, preview.disk.radiusM, preview.disk.heightM, 128, 1, false);
    const pillarGeometry = new THREE.CylinderGeometry(preview.pillar.radiusM, preview.pillar.radiusM, preview.pillar.heightM, 128, 1, false);
    const wallShape = new THREE.Shape();
    wallShape.absarc(0, 0, preview.wall.outerRadiusM, 0, Math.PI * 2, false);
    const wallHole = new THREE.Path();
    wallHole.absarc(0, 0, preview.wall.innerRadiusM, 0, Math.PI * 2, true);
    wallShape.holes.push(wallHole);
    const wallGeometry = new THREE.ExtrudeGeometry(wallShape, { depth: preview.wall.heightM, bevelEnabled: false, curveSegments: 128, steps: 1 });
    wallGeometry.translate(0, 0, -preview.wall.heightM / 2);
    wallGeometry.rotateX(-Math.PI / 2);

    const makeInstances = (name, geometry, material, placements) => {
      const mesh = new THREE.InstancedMesh(geometry, material, placements.length);
      mesh.name = name;
      mesh.userData = { structureInstanceCount: preview.structureTransforms.length };
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      const transform = new THREE.Object3D();
      placements.forEach((placement, index) => {
        transform.position.set(placement.xM, placement.yM, placement.zM);
        transform.rotation.set(0, placement.rotationYRad, 0);
        transform.scale.set(1, 1, 1);
        transform.updateMatrix();
        mesh.setMatrixAt(index, transform.matrix);
      });
      mesh.instanceMatrix.needsUpdate = true;
      mesh.computeBoundingBox();
      mesh.computeBoundingSphere();
      group.add(mesh);
      this.originals.set(mesh, material);
      const wireMaterial = new THREE.MeshBasicMaterial({ color: 0xa8f0db, wireframe: true, transparent: true, opacity: 0.2, depthWrite: false });
      const wire = new THREE.InstancedMesh(geometry, wireMaterial, placements.length);
      placements.forEach((placement, index) => {
        transform.position.set(placement.xM, placement.yM, placement.zM);
        transform.rotation.set(0, placement.rotationYRad, 0);
        transform.scale.set(1, 1, 1);
        transform.updateMatrix();
        wire.setMatrixAt(index, transform.matrix);
      });
      wire.instanceMatrix.needsUpdate = true;
      wire.visible = false;
      group.add(wire);
      this.wireOverlays.push(wire);
      return mesh;
    };
    makeInstances("Sky-elevator disks · PCG root array · live proxy", diskGeometry, materials.disk, preview.disk.transforms);
    makeInstances("Sky-elevator perimeter walls · PCG root array · live proxy", wallGeometry, materials.wall, preview.wall.transforms);
    makeInstances("Sky-elevator cables/cores · PCG root array · live proxy", pillarGeometry, materials.pillar, preview.pillar.transforms);
    const additionGeometryStats = [];
    for (const addition of preview.additions) {
      const geometry = addition.shape === "box"
        ? new THREE.BoxGeometry(addition.diameterM, addition.heightM, addition.diameterM)
        : new THREE.CylinderGeometry(addition.diameterM / 2, addition.diameterM / 2, addition.heightM, 48, 1, false);
      const material = new THREE.MeshStandardMaterial({ color: addition.color, roughness: 0.78, metalness: 0.08 });
      const mesh = new THREE.InstancedMesh(geometry, material, addition.transforms.length);
      mesh.name = `${addition.id} · radial wall towers · live proxy`;
      mesh.userData = { additionId: addition.id, additionKind: addition.kind };
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      const transform = new THREE.Object3D();
      addition.transforms.forEach((placement, index) => {
        transform.position.set(placement.xM, placement.yM, placement.zM);
        transform.rotation.set(0, placement.rotationYRad, 0);
        transform.updateMatrix();
        mesh.setMatrixAt(index, transform.matrix);
      });
      mesh.instanceMatrix.needsUpdate = true;
      mesh.computeBoundingBox();
      mesh.computeBoundingSphere();
      group.add(mesh);
      this.originals.set(mesh, material);

      const wireMaterial = new THREE.MeshBasicMaterial({ color: 0xa8f0db, wireframe: true, transparent: true, opacity: 0.2, depthWrite: false });
      const wire = new THREE.InstancedMesh(geometry, wireMaterial, addition.transforms.length);
      addition.transforms.forEach((placement, index) => {
        transform.position.set(placement.xM, placement.yM, placement.zM);
        transform.rotation.set(0, placement.rotationYRad, 0);
        transform.updateMatrix();
        wire.setMatrixAt(index, transform.matrix);
      });
      wire.instanceMatrix.needsUpdate = true;
      wire.visible = false;
      group.add(wire);
      this.wireOverlays.push(wire);
      additionGeometryStats.push({ geometry, count: addition.transforms.length });
    }
    const cityGeometryStats = [];
    if (preview.cityContext) {
      const context = preview.cityContext;
      const cityGroundMaterial = new THREE.MeshStandardMaterial({ color: 0x34373a, roughness: 1, metalness: 0 });
      const cityGround = new THREE.Mesh(new THREE.BoxGeometry(context.widthM, 0.5, context.depthM), cityGroundMaterial);
      cityGround.position.y = -0.25;
      cityGround.name = "Central Paris · meter-scale schematic ground";
      group.add(cityGround);
      this.originals.set(cityGround, cityGroundMaterial);
      cityGeometryStats.push({ geometry: cityGround.geometry, count: 1 });

      const blockGeometry = new THREE.BoxGeometry(1, 1, 1);
      const blockMaterial = new THREE.MeshStandardMaterial({ color: 0x8f8b82, roughness: 0.96, metalness: 0 });
      const blocks = new THREE.InstancedMesh(blockGeometry, blockMaterial, context.buildings.length);
      blocks.name = "Central Paris · deterministic schematic massing";
      const transform = new THREE.Object3D();
      context.buildings.forEach((building, index) => {
        transform.position.set(building.xM, building.heightM / 2, building.zM);
        transform.rotation.set(0, 0, 0);
        transform.scale.set(building.widthM, building.heightM, building.depthM);
        transform.updateMatrix();
        blocks.setMatrixAt(index, transform.matrix);
      });
      blocks.instanceMatrix.needsUpdate = true;
      blocks.computeBoundingBox();
      blocks.computeBoundingSphere();
      group.add(blocks);
      this.originals.set(blocks, blockMaterial);
      cityGeometryStats.push({ geometry: blockGeometry, count: context.buildings.length });

      const riverMaterial = new THREE.MeshStandardMaterial({ color: 0x315f79, roughness: 0.5, metalness: 0.05 });
      for (const [index, segment] of context.riverSegments.entries()) {
        const dx = segment.end.xM - segment.start.xM;
        const dz = segment.end.zM - segment.start.zM;
        const lengthM = Math.hypot(dx, dz);
        const geometry = new THREE.BoxGeometry(lengthM, 0.8, segment.widthM);
        const river = new THREE.Mesh(geometry, riverMaterial);
        river.position.set((segment.start.xM + segment.end.xM) / 2, 0.2, (segment.start.zM + segment.end.zM) / 2);
        river.rotation.y = -Math.atan2(dz, dx);
        river.name = `Seine reference segment ${index + 1}`;
        group.add(river);
        this.originals.set(river, riverMaterial);
        cityGeometryStats.push({ geometry, count: 1 });
      }

      const landmarkMaterial = new THREE.MeshStandardMaterial({ color: 0xc7bda9, roughness: 0.82, metalness: 0.02 });
      for (const landmark of context.landmarks) {
        const geometry = new THREE.BoxGeometry(landmark.widthM, landmark.heightM, landmark.depthM);
        const mesh = new THREE.Mesh(geometry, landmarkMaterial);
        mesh.position.set(landmark.xM, landmark.heightM / 2, landmark.zM);
        mesh.name = `${landmark.id.replaceAll("_", " ")} · scale reference`;
        group.add(mesh);
        this.originals.set(mesh, landmarkMaterial);
        cityGeometryStats.push({ geometry, count: 1 });
      }

      const eiffelMaterial = new THREE.MeshStandardMaterial({ color: 0x8c6b42, roughness: 0.62, metalness: 0.48, wireframe: true });
      const eiffelBodyGeometry = new THREE.CylinderGeometry(10, context.eiffelTower.widthM / 2, 276, 4, 8, false);
      const eiffelBody = new THREE.Mesh(eiffelBodyGeometry, eiffelMaterial);
      eiffelBody.position.set(context.eiffelTower.xM, 138, context.eiffelTower.zM);
      eiffelBody.rotation.y = Math.PI / 4;
      eiffelBody.name = "Eiffel Tower · 330m × 125m scale reference";
      group.add(eiffelBody);
      this.originals.set(eiffelBody, eiffelMaterial);
      const antennaGeometry = new THREE.CylinderGeometry(2, 5, 54, 12);
      const antenna = new THREE.Mesh(antennaGeometry, eiffelMaterial);
      antenna.position.set(context.eiffelTower.xM, 303, context.eiffelTower.zM);
      antenna.name = "Eiffel Tower antenna · scale reference";
      group.add(antenna);
      this.originals.set(antenna, eiffelMaterial);
      cityGeometryStats.push({ geometry: eiffelBodyGeometry, count: 1 }, { geometry: antennaGeometry, count: 1 });
    }
    this.originals.set(ground, materials.landscape);

    const box = new THREE.Box3().setFromObject(group);
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z) || 1;
    this.setFlyScale(maxDim);
    this.scene.remove(this.grid);
    disposeObject3D(this.grid);
    this.grid = new THREE.GridHelper(Math.max(preview.ground.widthM, preview.ground.depthM) * 1.35, 48, 0x59616b, 0x26262f);
    this.grid.visible = this.toggles.grid;
    this.grid.position.set(preview.ground.centreXM, 0, preview.ground.centreZM);
    this.scene.add(this.grid);
    this.axes.scale.setScalar(maxDim * 0.08);
    this.bboxHelper = new THREE.Box3Helper(box, 0xe8b34b);
    this.bboxHelper.visible = this.toggles.bbox;
    this.scene.add(this.bboxHelper);
    if (pose) this.restoreCameraPose(pose); else this.frame();
    this.setMode(this.mode);

    const triangleCount = (geometry) => Math.round((geometry.index?.count ?? geometry.attributes.position?.count ?? 0) / 3);
    return {
      triangles: triangleCount(ground.geometry)
        + preview.state.tiers.count * preview.structureTransforms.length * (triangleCount(diskGeometry) + triangleCount(wallGeometry) + triangleCount(pillarGeometry))
        + additionGeometryStats.reduce((sum, item) => sum + triangleCount(item.geometry) * item.count, 0)
        + cityGeometryStats.reduce((sum, item) => sum + triangleCount(item.geometry) * item.count, 0),
      vertices: ground.geometry.attributes.position.count
        + preview.state.tiers.count * preview.structureTransforms.length * (diskGeometry.attributes.position.count + wallGeometry.attributes.position.count + pillarGeometry.attributes.position.count)
        + additionGeometryStats.reduce((sum, item) => sum + item.geometry.attributes.position.count * item.count, 0)
        + cityGeometryStats.reduce((sum, item) => sum + item.geometry.attributes.position.count * item.count, 0),
      materials: 4 + preview.additions.length + (preview.cityContext ? 5 : 0),
      textures: 0,
      sizeCm: { x: size.x * 100, y: size.y * 100, z: size.z * 100 },
      sourceMeshCount: 4 + preview.additions.length + cityGeometryStats.length,
      instances: preview.instanceCount,
      previewKind: "stacked_megastructure_visualization",
      overallHeightM: preview.overallHeightM,
    };
  }

  focusStackedMegastructureTier(tierIndex = 0) {
    const preview = this.model?.userData?.preview;
    if (!preview) return;
    const index = THREE.MathUtils.clamp(Math.round(tierIndex), 0, preview.state.tiers.count - 1);
    const baseY = index * preview.state.tiers.verticalPitchM;
    const radius = preview.disk.radiusM;
    const target = new THREE.Vector3(preview.structureOffsetM.x, baseY + preview.state.tiers.disk.heightM + 3, preview.structureOffsetM.z);
    this.camera.position.set(target.x, target.y + 12, target.z + radius * 0.82);
    this.setCameraClipping({ far: Math.max(preview.state.tiers.verticalPitchM * 6, radius * 12) });
    this.controls.target.copy(target);
    this.camera.lookAt(target);
    if (!this.fly.enabled) this.controls.update();
  }

  focusMegastructureArray() {
    if (!this.model?.userData?.preview) return;
    this.setFlyMode(false);
    this.frame();
  }

  focusGeographicReference() {
    const context = this.model?.userData?.preview?.cityContext;
    if (!context) return;
    this.setFlyMode(false);
    const target = new THREE.Vector3(context.eiffelTower.xM, 130, context.eiffelTower.zM);
    const structureLength = Math.hypot(context.structureOffsetM.x, context.structureOffsetM.z) || 1;
    const awayX = -context.structureOffsetM.x / structureLength;
    const awayZ = -context.structureOffsetM.z / structureLength;
    const tangentX = -awayZ;
    const tangentZ = awayX;
    this.camera.position.set(
      target.x + awayX * 780 + tangentX * 620,
      430,
      target.z + awayZ * 780 + tangentZ * 620,
    );
    this.setCameraClipping({ far: 100_000 });
    this.controls.target.copy(target);
    this.camera.lookAt(target);
    this.controls.update();
  }

  setFallbackTerrain(terrain, state) {
    this.renderer.setTerrain?.(terrain, state);
  }

  /** Render the reusable source module by default. The optional assembly view
   * stays explicit so opening a structure never implies that it was placed. */
  loadRadialStructure(structure, { scope = "source_segment" } = {}) {
    this.clear();
    const preview = scope === "assembly"
      ? buildRadialStructurePreviewModel(structure)
      : buildRadialStructureSourceSegmentPreviewModel(structure);
    const group = new THREE.Group();
    group.name = `${preview.name} · ${preview.previewKind === "source_segment" ? "source segment" : "assembly"} preview`;
    group.userData = { structureId: preview.structureId, previewKind: preview.previewKind || "radial_assembly" };
    this.model = group;
    this.scene.add(group);

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(preview.geometry.positions, 3));
    for (const materialGroup of preview.geometry.groups) {
      geometry.addGroup(materialGroup.start, materialGroup.count, materialGroup.materialIndex);
    }
    geometry.computeVertexNormals();
    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();

    const materials = [
      new THREE.MeshStandardMaterial({ color: 0x59616b, roughness: 0.78, metalness: 0.22 }),
      new THREE.MeshStandardMaterial({ color: 0xc49a50, roughness: 0.9, metalness: 0.04 }),
    ];
    materials[0].name = "CordonStructure · visual proxy";
    materials[1].name = "CordonFloor · visual proxy";

    const mesh = new THREE.InstancedMesh(geometry, materials, preview.instances.length);
    mesh.name = `${preview.name} · ${preview.instances.length} source piece${preview.instances.length === 1 ? "" : "s"}`;
    mesh.instanceMatrix.setUsage(THREE.StaticDrawUsage);
    mesh.userData = { sourceMeshCount: 1, actorLabels: preview.instances.map((instance) => instance.actorLabel) };

    const wireMaterials = [0, 1].map(() => new THREE.MeshBasicMaterial({
      color: 0x9ce8d0,
      wireframe: true,
      transparent: true,
      opacity: 0.48,
      depthWrite: false,
    }));
    const wire = new THREE.InstancedMesh(geometry, wireMaterials, preview.instances.length);
    wire.name = "Connection-plane wire overlay";
    wire.visible = false;

    const transform = new THREE.Object3D();
    for (const instance of preview.instances) {
      transform.position.set(instance.positionM.x, instance.positionM.y, instance.positionM.z);
      transform.rotation.set(0, instance.threeRotationYRad, 0);
      transform.scale.set(1, 1, 1);
      transform.updateMatrix();
      mesh.setMatrixAt(instance.index, transform.matrix);
      wire.setMatrixAt(instance.index, transform.matrix);
    }
    mesh.instanceMatrix.needsUpdate = true;
    wire.instanceMatrix.needsUpdate = true;
    mesh.computeBoundingBox();
    mesh.computeBoundingSphere();
    wire.computeBoundingBox();
    wire.computeBoundingSphere();
    group.add(mesh, wire);

    // Lane/median lines belong to the review surface only. They are separate
    // Three.js lines rather than another mesh group, so the source segment
    // retains its two material slots and can never be mistaken for an Unreal
    // road-material or collision instruction.
    const roadGuides = preview.roadGuides;
    if (roadGuides?.segments?.length) {
      const guideGroup = new THREE.Group();
      guideGroup.name = "Road layout · planning guides";
      guideGroup.userData = {
        role: roadGuides.role,
        axis: roadGuides.axis,
        laneCount: roadGuides.laneCount,
        laneWidthM: roadGuides.laneWidthM,
        shoulderWidthM: roadGuides.shoulderWidthM,
        medianWidthM: roadGuides.medianWidthM,
        usedWidthM: roadGuides.usedWidthM,
        remainingClearanceM: roadGuides.remainingClearanceM,
        guideCount: roadGuides.segments.length,
      };
      const guideStyles = {
        shoulder_boundary: { color: 0x8fe3ef, opacity: 0.72, label: "Shoulder boundary" },
        lane_divider: { color: 0xf2ead7, opacity: 0.9, label: "Lane divider" },
        median_edge: { color: 0xf0ab4b, opacity: 0.96, label: "Median edge" },
      };
      const byKind = new Map();
      for (const segment of roadGuides.segments) {
        const positions = byKind.get(segment.kind) || [];
        positions.push(
          segment.startM.x, segment.startM.y, segment.startM.z,
          segment.endM.x, segment.endM.y, segment.endM.z,
        );
        byKind.set(segment.kind, positions);
      }
      const guideLines = [];
      for (const [kind, positions] of byKind) {
        const style = guideStyles[kind] || guideStyles.lane_divider;
        const guideGeometry = new THREE.BufferGeometry();
        guideGeometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
        const guideMaterial = new THREE.LineBasicMaterial({
          color: style.color,
          transparent: true,
          opacity: style.opacity,
          depthTest: true,
          depthWrite: false,
        });
        guideMaterial.name = `${style.label} · planning proxy`;
        guideLines.push({ kind, style, geometry: guideGeometry, material: guideMaterial });
      }
      for (const instance of preview.instances) {
        const instanceGuides = new THREE.Group();
        instanceGuides.name = `Road layout · ${instance.actorLabel}`;
        instanceGuides.position.set(instance.positionM.x, instance.positionM.y, instance.positionM.z);
        instanceGuides.rotation.set(0, instance.threeRotationYRad, 0);
        for (const guideLine of guideLines) {
          const line = new THREE.LineSegments(guideLine.geometry, guideLine.material);
          line.name = `${guideLine.style.label} · planning proxy`;
          line.userData = { role: "planning_proxy", kind: guideLine.kind };
          instanceGuides.add(line);
        }
        guideGroup.add(instanceGuides);
      }
      group.add(guideGroup);
    }

    this.originals.set(mesh, materials);
    this.wireOverlays.push(wire);

    const box = new THREE.Box3().setFromObject(group);
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z) || 1;
    this.setFlyScale(maxDim);
    this.scene.remove(this.grid);
    this.grid = new THREE.GridHelper(maxDim * 1.35, 48, 0x59616b, 0x26262f);
    this.grid.visible = this.toggles.grid;
    this.grid.position.y = box.min.y;
    this.scene.add(this.grid);
    this.axes.scale.setScalar(maxDim * 0.08);
    this.bboxHelper = new THREE.Box3Helper(box, 0xe8b34b);
    this.bboxHelper.visible = this.toggles.bbox;
    this.scene.add(this.bboxHelper);
    this.frame();
    if (preview.previewKind === "source_segment" && preview.longitudinalOpening) this.focusRadialOpening();
    this.setMode(this.mode);

    return {
      triangles: preview.geometry.sourceTriangles * preview.instances.length,
      vertices: preview.geometry.sourceVertices * preview.instances.length,
      materials: materials.length,
      textures: 0,
      sizeCm: { x: size.x * 100, y: size.y * 100, z: size.z * 100 },
      sourceMeshCount: preview.sourceMeshCount,
      instances: preview.instances.length,
      maximumSeamErrorCm: preview.maximumSeamErrorCm,
      previewKind: preview.previewKind || "radial_assembly",
      planningGuideCount: roadGuides?.segments?.length || 0,
    };
  }

  /** Compose the whole world from layout + manifests + assets — the level
   * as data, rendered without UE. cm→m; layout x→three x, layout y→three z
   * (so north reads as away from the default camera), z-up → y-up. */
  async loadWorld(world) {
    this.clear();
    const generation = this.loadGeneration;
    const group = new THREE.Group();
    this.model = group;
    this.scene.add(group);

    const layout = world.layout?.doc;
    const cellColors = [0xe8b34b, 0x5c9de0, 0x4bc47f, 0xe05c5c, 0xb07ce0, 0x50c8c0, 0xd0a050, 0x8090e0];
    const assetsByUid = new Map(world.assets.map((a) => [a.assetUid, a]));
    const glbCache = new Map();
    let placements = 0, placeholders = 0, loaded = 0;

    if (layout) {
      const w = layout.worldSizeCm.x / 100, h = layout.worldSizeCm.y / 100;
      const ground = new THREE.Mesh(
        new THREE.PlaneGeometry(w, h),
        new THREE.MeshStandardMaterial({ color: 0x23232b, roughness: 1 }),
      );
      ground.rotation.x = -Math.PI / 2;
      ground.position.set(w / 2, -0.005, h / 2);
      group.add(ground);

      // blockout (greybox) solids — structural geometry rendered as grey
      // primitives, exactly like an in-engine blockout pass. cm→m, z-up→y-up.
      const solidMat = new THREE.MeshStandardMaterial({ color: 0x8d8d99, roughness: 0.95, side: THREE.DoubleSide });
      const coverMat = new THREE.MeshStandardMaterial({ color: 0xa8a090, roughness: 0.95, side: THREE.DoubleSide });
      for (const s of world.blockout?.doc?.solids ?? []) {
        const sx = s.size.x / 100, sy = s.size.z / 100, sz = s.size.y / 100; // world z(height)→three y
        const px = s.pos.x / 100, py = s.pos.z / 100, pz = s.pos.y / 100;
        let mesh;
        if (s.kind === "ramp") {
          mesh = new THREE.Mesh(makeRampGeometry(sx, sy, sz, s.rampDir ?? "+x"), solidMat);
        } else {
          mesh = new THREE.Mesh(new THREE.BoxGeometry(sx, sy, sz), s.role === "cover" ? coverMat : solidMat);
          mesh.position.set(sx / 2, sy / 2, sz / 2); // BoxGeometry is centered; solids use min-corner
        }
        const holder = new THREE.Group();
        holder.add(mesh);
        holder.position.set(px, py, pz);
        holder.userData = { solidId: s.solidId, role: s.role };
        group.add(holder);
      }

      layout.cells.forEach((cell, i) => {
        const b = cell.bounds;
        const x0 = b.x / 100, z0 = b.y / 100, x1 = (b.x + b.w) / 100, z1 = (b.y + b.h) / 100;
        const pts = [
          new THREE.Vector3(x0, 0.02, z0), new THREE.Vector3(x1, 0.02, z0),
          new THREE.Vector3(x1, 0.02, z1), new THREE.Vector3(x0, 0.02, z1),
        ];
        const line = new THREE.LineLoop(
          new THREE.BufferGeometry().setFromPoints(pts),
          new THREE.LineBasicMaterial({ color: cellColors[i % cellColors.length] }),
        );
        group.add(line);
        group.add(makeLabel(cell.name, (x0 + x1) / 2, (z0 + z1) / 2, cellColors[i % cellColors.length]));
      });

      // Frame the structural pass before waiting on any catalog GLBs. Large
      // worlds can spend several seconds resolving generated assets; the
      // user should see a correctly framed greybox during that work instead
      // of the previous camera position clipped inside a wall.
      this.frame();
    }

    let cancelled = false;
    placementLoop: for (const m of world.manifests) {
      for (const p of m.doc?.placements ?? []) {
        if (generation !== this.loadGeneration) {
          cancelled = true;
          break placementLoop;
        }
        placements++;
        const asset = assetsByUid.get(p.assetUid);
        const px = p.position.x / 100, py = p.position.z / 100, pz = p.position.y / 100;
        let node;
        if (asset?.glbKey) {
          if (!glbCache.has(asset.glbKey)) {
            glbCache.set(asset.glbKey, this.loader.loadAsync(this.fileUrl(asset.glbKey)).then((g) => g.scene));
          }
          try {
            const source = await glbCache.get(asset.glbKey);
            if (generation !== this.loadGeneration) {
              disposeObject3D(source);
              cancelled = true;
              break placementLoop;
            }
            node = source.clone(true);
            loaded++;
          } catch {
            node = null;
          }
        }
        if (!node) {
          placeholders++;
          const d = asset?.dimsCm ?? { x: 100, y: 100, z: 100 };
          node = new THREE.LineSegments(
            new THREE.EdgesGeometry(new THREE.BoxGeometry(d.x / 100, d.z / 100, d.y / 100)),
            new THREE.LineDashedMaterial({ color: 0x8b8b9a, dashSize: 0.05, gapSize: 0.04 }),
          );
          node.computeLineDistances?.();
          node.position.y = d.z / 200; // box is centered; placements sit on ground
        }
        const holder = new THREE.Group();
        holder.add(node);
        holder.position.set(px, py, pz);
        holder.rotation.y = -THREE.MathUtils.degToRad(p.yawDeg ?? 0);
        holder.scale.setScalar(p.scale ?? 1);
        holder.userData = { slotKey: p.slotKey, asset: asset?.displayName };
        group.add(holder);
      }
    }

    if (cancelled || generation !== this.loadGeneration) {
      return { cancelled: true, cells: 0, solids: 0, placements, loaded, placeholders };
    }

    // collect meshes so render modes keep working (no wire overlays here)
    this.originals.clear();
    this.wireOverlays = [];
    const meshes = [];
    group.traverse((o) => {
      if (o.isMesh) meshes.push(o);
    });
    for (const o of meshes) this.originals.set(o, o.material);

    const box = new THREE.Box3().setFromObject(group);
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z) || 1;
    this.setFlyScale(maxDim);
    this.scene.remove(this.grid);
    disposeObject3D(this.grid);
    this.grid = new THREE.GridHelper(maxDim * 1.5, 30, 0x444455, 0x26262f);
    this.grid.visible = this.toggles.grid;
    this.grid.position.set(size.x / 2, 0, size.z / 2);
    this.scene.add(this.grid);
    this.bboxHelper = new THREE.Box3Helper(box, 0xe8b34b);
    this.bboxHelper.visible = this.toggles.bbox;
    this.scene.add(this.bboxHelper);
    this.frame();
    this.setMode(this.mode === "wireover" ? "shaded" : this.mode);

    return { cells: layout?.cells.length ?? 0, solids: world.blockout?.doc?.solids?.length ?? 0, placements, loaded, placeholders };
  }

  /** Raycast a normalized canvas position (0..1, top-left origin) into the
   * loaded model. Returns {point, normal, hit} in model/world units (m) —
   * the anchor for pixel-exact decal placement from a drawing. */
  raycastModel(nx, ny) {
    if (!this.model) return null;
    const ndc = new THREE.Vector2(nx * 2 - 1, -(ny * 2 - 1));
    this._raycaster.setFromCamera(ndc, this.camera);
    const hit = this._raycaster.intersectObject(this.model, true)[0];
    if (!hit) return null;
    const normal = hit.face
      ? hit.face.normal.clone().transformDirection(hit.object.matrixWorld)
      : new THREE.Vector3(0, 1, 0);
    return { point: [hit.point.x, hit.point.y, hit.point.z], normal: [normal.x, normal.y, normal.z] };
  }

  /** Feedback pins in world space. pins: [{id, number, xCm, yCm, zCm, color}]
   * — world cm, z = floor height at the spot; the pin floats above a stalk. */
  setFeedbackPins(pins) {
    for (const child of [...this.pinGroup.children]) disposeObject3D(child);
    this.pinGroup.clear();
    for (const p of pins) {
      const holder = new THREE.Group();
      holder.userData = { pinId: p.id };
      const px = p.xCm / 100, py = p.zCm / 100, pz = p.yCm / 100;
      const h = 2.2; // pin head floats 2.2m above the floor
      const sprite = makePin(p.number, p.color);
      sprite.position.set(px, py + h, pz);
      holder.add(sprite);
      const stalk = new THREE.Line(
        new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(px, py, pz), new THREE.Vector3(px, py + h - 0.4, pz)]),
        new THREE.LineBasicMaterial({ color: p.color, transparent: true, opacity: 0.7 }),
      );
      holder.add(stalk);
      this.pinGroup.add(holder);
    }
  }

  /** Load a GLB and return stats {triangles, vertices, materials, textures, sizeCm}.
   * opts.keepCamera: swap the mesh WITHOUT reframing — for old/new version
   * comparison the camera must stay exactly where the user left it. */
  async load(url, opts = {}) {
    this.clear();
    const generation = this.loadGeneration;
    const gltf = await this.loader.loadAsync(url);
    if (generation !== this.loadGeneration) {
      disposeObject3D(gltf.scene);
      throw cancelledLoadError();
    }
    this.model = gltf.scene;
    this.scene.add(this.model);

    // stats + originals + wire overlays
    let triangles = 0, vertices = 0;
    const materials = new Set(), textures = new Set();
    const wireMat = new THREE.MeshBasicMaterial({
      color: 0x30e0a0, wireframe: true, transparent: true, opacity: 0.25,
      depthTest: true, polygonOffset: true, polygonOffsetFactor: -1,
    });
    // collect first — adding overlay children during traverse() would make
    // the traversal visit them and recurse forever
    const meshes = [];
    this.model.traverse((o) => {
      if (o.isMesh) meshes.push(o);
    });
    for (const o of meshes) {
      const pos = o.geometry.attributes.position;
      vertices += pos?.count ?? 0;
      triangles += (o.geometry.index ? o.geometry.index.count : pos?.count ?? 0) / 3;
      const mats = Array.isArray(o.material) ? o.material : [o.material];
      for (const m of mats) {
        materials.add(m);
        for (const k of ["map", "normalMap", "roughnessMap", "metalnessMap", "aoMap", "emissiveMap"])
          if (m[k]) textures.add(m[k]);
      }
      this.originals.set(o, o.material);
      const wire = new THREE.Mesh(o.geometry, wireMat);
      wire.visible = false;
      o.add(wire);
      this.wireOverlays.push(wire);
    }

    // frame + fit helpers to model size
    const box = new THREE.Box3().setFromObject(this.model);
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z) || 1;
    this.setFlyScale(maxDim);
    this.scene.remove(this.grid);
    disposeObject3D(this.grid);
    this.grid = new THREE.GridHelper(maxDim * 4, 40, 0x444455, 0x26262f);
    this.grid.visible = this.toggles.grid;
    this.grid.position.y = box.min.y;
    this.scene.add(this.grid);
    this.axes.scale.setScalar(maxDim);
    this.bboxHelper = new THREE.Box3Helper(box, 0xe8b34b);
    this.bboxHelper.visible = this.toggles.bbox;
    this.scene.add(this.bboxHelper);
    if (!opts.keepCamera) this.frame();
    this.setMode(this.mode);

    return {
      triangles: Math.round(triangles),
      vertices,
      materials: materials.size,
      textures: textures.size,
      // glTF units are meters
      sizeCm: { x: size.x * 100, y: size.y * 100, z: size.z * 100 },
    };
  }

  frame() {
    if (!this.model) return;
    const box = new THREE.Box3().setFromObject(this.model);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z) || 1;
    const dist = maxDim / (2 * Math.tan((this.camera.fov * Math.PI) / 360));
    const dir = new THREE.Vector3(1, 0.45, 1).normalize();
    this.camera.position.copy(center).addScaledVector(dir, dist * 1.5);
    this.setCameraClipping({ far: maxDim * 100 });
    this.controls.target.copy(center);
    if (this.fly.enabled) this.camera.lookAt(center);
    else this.controls.update();
  }

  /** Put an end-to-end opening at a useful inspection scale. Framing a
   * 402 m source piece correctly makes its 15 × 18 m end face too small to
   * judge, so tunnel variants start at the positive-X connection plane. */
  focusRadialOpening(end = "positive") {
    if (!this.model) return;
    const box = new THREE.Box3().setFromObject(this.model);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const crossSection = Math.max(size.y, size.z, 1);
    const positive = end !== "negative";
    const endX = positive ? box.max.x : box.min.x;
    const direction = positive ? 1 : -1;
    const target = new THREE.Vector3(endX - direction * crossSection * 2.5, center.y, center.z);
    this.camera.position.set(
      endX + direction * crossSection * 2.2,
      center.y + crossSection * 0.12,
      center.z + crossSection * 0.04,
    );
    this.setCameraClipping({ far: Math.max(size.x * 4, crossSection * 100) });
    this.controls.target.copy(target);
    if (this.fly.enabled) this.camera.lookAt(target);
    else this.controls.update();
  }

  setMode(mode) {
    this.mode = mode;
    if (!this.model) return;
    for (const material of this.transientModeMaterials) material.dispose?.();
    this.transientModeMaterials.clear();
    for (const wire of this.wireOverlays) wire.visible = mode === "wireover";
    for (const [mesh, orig] of this.originals) {
      const om = Array.isArray(orig) ? orig[0] : orig;
      const material = this.materialFor(mode, orig, om);
      mesh.material = material;
      if (material !== orig) {
        const mats = Array.isArray(material) ? material : [material];
        for (const item of mats) this.transientModeMaterials.add(item);
      }
    }
  }

  materialFor(mode, orig, om) {
    switch (mode) {
      case "shaded":
      case "wireover":
        return orig;
      case "wireframe":
        return new THREE.MeshBasicMaterial({ color: 0x30e0a0, wireframe: true });
      case "clay":
        return new THREE.MeshStandardMaterial({ color: 0xb8b0a8, roughness: 0.85, metalness: 0 });
      case "normals":
        return new THREE.MeshNormalMaterial();
      case "uv":
        return new THREE.MeshBasicMaterial({ map: this.checker });
      case "basecolor":
        return new THREE.MeshBasicMaterial({ map: om.map ?? null, color: om.map ? 0xffffff : (om.color ?? 0xffffff) });
      case "normalmap":
        return om.normalMap
          ? new THREE.MeshBasicMaterial({ map: om.normalMap })
          : new THREE.MeshBasicMaterial({ color: 0x8080ff });
      case "roughness":
        return channelMaterial(om.roughnessMap, 1, om.roughness ?? 1);
      case "metallic":
        return channelMaterial(om.metalnessMap, 2, om.metalness ?? 0);
      case "ao":
        return channelMaterial(om.aoMap, 0, 1);
      case "emissive":
        return om.emissiveMap
          ? new THREE.MeshBasicMaterial({ map: om.emissiveMap })
          : new THREE.MeshBasicMaterial({ color: om.emissive ?? 0x000000 });
      default:
        return orig;
    }
  }

  setToggle(name, on) {
    this.toggles[name] = on;
    if (name === "grid") this.grid.visible = on;
    if (name === "axes") this.axes.visible = on;
    if (name === "bbox" && this.bboxHelper) this.bboxHelper.visible = on;
    if (name === "envBackground") this.scene.background = on ? this.envTexture : this.bgColor;
  }

  setExposure(v) {
    this.renderer.toneMappingExposure = v;
  }

  screenshot() {
    return this.renderer.domElement.toDataURL("image/png");
  }

  /** Distinct textures of the loaded model, for the Textures tab. */
  getTextures() {
    const seen = new Map();
    if (!this.model) return [];
    for (const orig of this.originals.values()) {
      const m = Array.isArray(orig) ? orig[0] : orig;
      for (const [slot, label] of [
        ["map", "base color"],
        ["normalMap", "normal"],
        ["roughnessMap", "roughness (G) / metallic (B)"],
        ["metalnessMap", "metallic (packed)"],
        ["aoMap", "ambient occlusion"],
        ["emissiveMap", "emissive"],
      ]) {
        const tex = m?.[slot];
        if (tex?.image && !seen.has(tex.uuid + slot)) seen.set(tex.uuid + slot, { label, tex });
      }
    }
    // dedupe the glTF-packed metallicRoughness texture shown twice
    const out = [];
    const usedTex = new Set();
    for (const { label, tex } of seen.values()) {
      const key = tex.uuid;
      if (usedTex.has(key) && label === "metallic (packed)") continue;
      usedTex.add(key);
      out.push({ label, image: tex.image });
    }
    return out;
  }
}
